import { useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useVacancyStore } from '../store/vacancyStore';

const RECRUITER_VACANCY_PATH_RE = /^\/recruiter\/vacancy\/(?:(?:edit|edit-data|edit-questions)\/)?(\d+)\/?$/;

const parseVacancyId = (raw: string | null | undefined): number | null => {
  if (!raw) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

export const getRouteVacancyId = (pathname: string, search: string): number | null => {
  const positionId = parseVacancyId(new URLSearchParams(search).get('positionId'));
  if (positionId) return positionId;

  const vacancyMatch = pathname.match(RECRUITER_VACANCY_PATH_RE);
  return parseVacancyId(vacancyMatch?.[1]);
};

export const useSelectedVacancyRouteSync = () => {
  const location = useLocation();
  const setSelectedVacancyById = useVacancyStore((state) => state.setSelectedVacancyById);

  const routeVacancyId = useMemo(
    () => getRouteVacancyId(location.pathname, location.search),
    [location.pathname, location.search]
  );
  const lastHandledRouteVacancyIdRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!routeVacancyId) {
      lastHandledRouteVacancyIdRef.current = null;
      return;
    }

    const { selectedVacancyId, selectedVacancy } = useVacancyStore.getState();
    const storeMatchesRoute =
      selectedVacancyId === routeVacancyId && selectedVacancy?.id === routeVacancyId;

    if (lastHandledRouteVacancyIdRef.current === routeVacancyId && storeMatchesRoute) {
      return;
    }

    lastHandledRouteVacancyIdRef.current = routeVacancyId;

    if (storeMatchesRoute) {
      return;
    }

    setSelectedVacancyById(routeVacancyId);
  }, [routeVacancyId, setSelectedVacancyById]);
};
